import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import {
  CpuChipIcon,
  CogIcon,
  ChartBarIcon,
  GlobeAltIcon,
  ArrowRightIcon,
  CheckIcon
} from '@heroicons/react/24/outline';

interface Service {
  id: string;
  icon: React.ComponentType<any>;
  title: string;
  description: string;
  features: string[];
  color: string;
  gradient: string;
}

import { useNavigate } from 'react-router-dom';

const ServiceCards: React.FC = () => {
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [expandedCard, setExpandedCard] = useState<string | null>(null);
  const [ref, inView] = useInView({
    threshold: 0.1,
    triggerOnce: true,
  });

  const services: Service[] = [
    {
      id: 'ai-implementation',
      icon: CpuChipIcon,
      title: t('services.aiImplementation.title'),
      description: t('services.aiImplementation.description'),
      features: [
        t('services.aiImplementation.feature1'),
        t('services.aiImplementation.feature2'),
        t('services.aiImplementation.feature3'),
        t('services.aiImplementation.feature4')
      ],
      color: 'blue',
      gradient: 'from-blue-500 to-cyan-500'
    },
    {
      id: 'business-automation',
      icon: CogIcon,
      title: t('services.businessAutomation.title'),
      description: t('services.businessAutomation.description'),
      features: [
        t('services.businessAutomation.feature1'),
        t('services.businessAutomation.feature2'),
        t('services.businessAutomation.feature3'),
        t('services.businessAutomation.feature4')
      ],
      color: 'purple',
      gradient: 'from-purple-500 to-pink-500'
    },
    {
      id: 'data-pipelines',
      icon: ChartBarIcon,
      title: t('services.dataPipelines.title'),
      description: t('services.dataPipelines.description'),
      features: [
        t('services.dataPipelines.feature1'),
        t('services.dataPipelines.feature2'),
        t('services.dataPipelines.feature3'),
        t('services.dataPipelines.feature4')
      ],
      color: 'green',
      gradient: 'from-green-500 to-emerald-500'
    },
    {
      id: 'cross-cultural-consulting',
      icon: GlobeAltIcon,
      title: t('services.crossCulturalConsulting.title'),
      description: t('services.crossCulturalConsulting.description'),
      features: [
        t('services.crossCulturalConsulting.feature1'),
        t('services.crossCulturalConsulting.feature2'),
        t('services.crossCulturalConsulting.feature3'),
        t('services.crossCulturalConsulting.feature4')
      ],
      color: 'orange',
      gradient: 'from-orange-500 to-red-500'
    }
  ];

  const handleCardClick = (serviceId: string) => {
    setExpandedCard(expandedCard === serviceId ? null : serviceId);
  };

  return (
    <section ref={ref} className="space-y-8">
      <motion.header
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6 }}
        className="text-center"
      >
        <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
          {t('services.title')}
        </h2>
        <p className="text-lg text-gray-300 max-w-3xl mx-auto">
          {t('services.subtitle')}
        </p>
      </motion.header>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {services.map((service, index) => {
          const Icon = service.icon;
          const isExpanded = expandedCard === service.id;
          
          return (
            <motion.div
              key={service.id}
              initial={{ opacity: 0, y: 30 }}
              animate={inView ? { opacity: 1, y: 0 } : {}}
              transition={{ duration: 0.6, delay: index * 0.1 }}
              className="relative group"
            >
              <motion.div
                layout
                className={`
                  bg-gray-800 border border-gray-700 rounded-2xl p-6 cursor-pointer
                  transition-all duration-300 hover:border-gray-600
                  ${isExpanded ? 'ring-2 ring-primary-500/50' : ''}
                `}
                onClick={() => handleCardClick(service.id)}
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                {/* Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex items-center space-x-4">
                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${service.gradient} p-3 group-hover:scale-110 transition-transform duration-300`}>
                      <Icon className="w-full h-full text-white" />
                    </div>
                    <div>
                      <h3 className="text-xl font-semibold text-white group-hover:text-primary-300 transition-colors">
                        {service.title}
                      </h3>
                    </div>
                  </div>
                  
                  <motion.div
                    animate={{ rotate: isExpanded ? 90 : 0 }}
                    transition={{ duration: 0.3 }}
                    className="text-gray-400 group-hover:text-primary-400 transition-colors"
                  >
                    <ArrowRightIcon className="w-5 h-5" />
                  </motion.div>
                </div>

                {/* Description */}
                <p className="text-gray-300 mb-4 leading-relaxed">
                  {service.description}
                </p>

                {/* Expanded Content */}
                <AnimatePresence>
                  {isExpanded && (
                    <motion.div
                      initial={{ opacity: 0, height: 0 }}
                      animate={{ opacity: 1, height: 'auto' }}
                      exit={{ opacity: 0, height: 0 }}
                      transition={{ duration: 0.3 }}
                      className="border-t border-gray-700 pt-4 mt-4"
                    >
                      <h4 className="text-white font-semibold mb-3">
                        {t('services.keyFeatures')}
                      </h4>
                      <ul className="space-y-2">
                        {service.features.map((feature, featureIndex) => (
                          <motion.li
                            key={featureIndex}
                            initial={{ opacity: 0, x: -20 }}
                            animate={{ opacity: 1, x: 0 }}
                            transition={{ duration: 0.3, delay: featureIndex * 0.1 }}
                            className="flex items-start space-x-3"
                          >
                            <div className={`w-5 h-5 rounded-full bg-gradient-to-br ${service.gradient} p-1 mt-0.5 flex-shrink-0`}>
                              <CheckIcon className="w-full h-full text-white" />
                            </div>
                            <span className="text-gray-300 text-sm">
                              {feature}
                            </span>
                          </motion.li>
                        ))}
                      </ul>
                      
                      <div className="mt-6">
                        <button className="btn-primary text-sm px-6 py-2">
                          {t('services.learnMore')}
                        </button>
                      </div>
                    </motion.div>
                  )}
                </AnimatePresence>

                {/* Hover Effect Overlay */}
                <div className={`absolute inset-0 bg-gradient-to-br ${service.gradient} opacity-0 group-hover:opacity-5 rounded-2xl transition-opacity duration-300 pointer-events-none`} />
              </motion.div>
            </motion.div>
          );
        })}
      </div>

      {/* Call to Action */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={inView ? { opacity: 1, y: 0 } : {}}
        transition={{ duration: 0.6, delay: 0.8 }}
        className="text-center pt-8"
      >
        <div className="bg-gradient-to-r from-primary-500/10 to-secondary-500/10 border border-primary-500/20 rounded-2xl p-8">
          <h3 className="text-2xl font-bold text-white mb-4">
            {t('services.ctaTitle')}
          </h3>
          <p className="text-gray-300 mb-6 max-w-2xl mx-auto">
            {t('services.ctaDescription')}
          </p>
          <button 
            className="btn-primary text-lg px-8 py-4"
            onClick={() => navigate(`/${i18n.language || 'en'}/consultation`)}
          >
            {t('services.ctaButton')}
          </button>
        </div>
      </motion.div>
    </section>
  );
};

export default ServiceCards;